import type { ApolloResolverContext } from '@edge/spark/io-apollo'
import { getActiveAuctionId } from '@edge/spark/io-roles'
import { filterMaybe } from '@edge/spark/utils'

import {
    type MutationResolvers,
    CreateAgentContactDocument as mutation,
    type CreateAgentContactMutation,
    type GetAgentByIdQuery,
    GetAgentByIdDocument as agentByIdQuery,
} from '../generated/graphql'

const createContactResolver: MutationResolvers<ApolloResolverContext>['createContact'] = async (
    _root,
    { input },
    { cache, client },
) => {
    if (!client) {
        throw new Error('createContact: Missing ApolloClient')
    }

    const auctionId = getActiveAuctionId()

    if (!auctionId) {
        throw new Error('createContact: Missing auctionId')
    }

    const tempId = Math.random() * 1e17
    const createAgentContact = {
        id: tempId,
        userName: '', // placeholder - userName not in Contact input
        contactedAt: input.contactedAt,
        note: input.note || '',
        statusCode: input.statusCode,
        typeCode: input.typeCode,
        __typename: 'Contact' as const,
    }

    const res = await client.mutate<CreateAgentContactMutation>({
        mutation,
        variables: input,
        optimisticResponse: { createAgentContact },
        update: (store, fetchResult): void => {
            if (!fetchResult?.data?.createAgentContact) {
                throw new Error('Missing createAgentContact')
            }

            // get cached agent info
            const agentCacheData = store.readQuery<GetAgentByIdQuery>({
                query: agentByIdQuery,
                variables: {
                    agentId: input.agentId,
                    auctionId,
                },
            })
            const { contacts } = agentCacheData?.auction?.agents?.byId || {}
            const contactList = contacts?.list?.filter(filterMaybe) || []
            const updatedList = [fetchResult.data.createAgentContact, ...contactList]

            // update cached agent contact logs
            store.writeData({
                data: {
                    [`auction({"id":"${auctionId}"})`]: {
                        [`agents({"filter":{"id":${input.agentId}}})`]: {
                            byId: {
                                contacts: {
                                    count: updatedList.length + 1,
                                    list: updatedList,
                                    __typename: 'ContactList',
                                },
                                __typename: 'Agent',
                            },
                            __typename: 'Agents',
                        },
                        __typename: 'Auction',
                    },
                },
            })
        },
    })

    return res.data?.createAgentContact || null
}

export default createContactResolver
